// @flow
import React, { useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import Typography from "../Typography";
import BasicButton from "../BasicButton";
import ConditionalWrapper from "../ConditionalWrapper";
import useModalHook from "../../hooks/useModalHook";
import useMatchMediaMatches from "../../hooks/useMatchMediaMatches";
import useOutsideClickHandler from "../../hooks/useOutsideClickHandler";
import Radio from "../Radio";
import SearchFormFullscreenHeader from "../SearchFormFullscreenHeader";
import ChevronIcon from "../Icons/ChevronIcon";

import sharedStyles from "../SearchFormDateInput/searchFormDateInput.module.scss";
import styles from "./searchFormMonthlyDurationInput.module.scss";

type T = (string, string) => string;

type Props = {
  selected: string,
  onChange: (string) => void,
  t: T
};

const optionData = (t) => {
  return {
    WEEKDAYS_ONLY: {
      value: "WEEKDAYS_ONLY",
      name: t("searchForm:weekdaysOnlyDurationName", "Mon-Fri only"),
      description: t(
        "searchForm:weekdaysOnlyDurationDescription",
        "Just reserve the space for week days."
      )
    },
    STANDARD: {
      value: "STANDARD",
      name: t("searchForm:standardDurationName", "7 days per week"),
      description: t(
        "searchForm:standardDurationDescription",
        "Guarantee the space for any day of the week."
      )
    }
  };
};

const cypress = "search-form-monthly-duration-input";

const SearchFormMonthlyDurationInput = ({ selected, onChange, t }: Props) => {
  const ref = useRef();
  const [pickerOpen, setPickerOpen] = useState(false);
  const isMobileLayout = useMatchMediaMatches("(max-width: 639px)");

  const showInWrapper = pickerOpen && isMobileLayout;
  useModalHook(showInWrapper, "modal-open");

  const onOutsideClick = useCallback(() => {
    setPickerOpen(false);
  }, [setPickerOpen]);
  useOutsideClickHandler(ref, onOutsideClick, pickerOpen);

  const onCancel = useCallback(() => {
    setPickerOpen(false);
  }, [setPickerOpen]);

  const onSelectItem = useCallback(
    (value) => {
      setPickerOpen(false);
      onChange(value);
    },
    [onChange]
  );

  const optionsObject = optionData(t);
  const optionDataValues = Object.values(optionsObject);

  return (
    <div className={`${sharedStyles.alignRight} ${sharedStyles.base}`}>
      <BasicButton
        className={sharedStyles.triggerButton}
        onClick={() => {
          setPickerOpen(true);
        }}
        cypress={cypress}
      >
        <span className={sharedStyles.buttonContent}>
          <Typography color="brand" className={sharedStyles.label}>
            {t("searchForm:monthlyDuration", "Monthly Duration")}
          </Typography>

          <Typography className={sharedStyles.dateText}>
            {optionsObject[selected].name}
          </Typography>

          <ChevronIcon direction="down" className={sharedStyles.chevron} />
        </span>
      </BasicButton>

      {pickerOpen && (
        <ConditionalWrapper
          condition={showInWrapper}
          wrapper={(children) => {
            return createPortal(children, document.getElementById("portal"));
          }}
        >
          <div
            ref={ref}
            className={
              showInWrapper ? styles.fullscreenModal : styles.pickerContainer
            }
            data-cy={`${cypress}-dropdown`}
          >
            {showInWrapper && (
              <SearchFormFullscreenHeader
                header="Monthly Duration"
                onCancel={onCancel}
              />
            )}

            {optionDataValues.map(({ value, name, description }) => {
              const isSelectedOption = selected === value;
              return (
                <BasicButton key={value} onClick={() => onSelectItem(value)}>
                  <div
                    className={styles.optionItem}
                    data-selected={isSelectedOption}
                  >
                    <div className={styles.optionTextContainer}>
                      <Typography>
                        <div className={styles.optionLabel}>{name}</div>
                      </Typography>
                      <Typography>
                        <div className={styles.optionDescription}>
                          {description}
                        </div>
                      </Typography>
                    </div>

                    <Radio
                      name="monthlyDuration"
                      checked={isSelectedOption}
                      onChange={() => {}}
                      value={value}
                      className={styles.optionRadio}
                    />
                  </div>
                </BasicButton>
              );
            })}
          </div>
        </ConditionalWrapper>
      )}
    </div>
  );
};

export default SearchFormMonthlyDurationInput;
